import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, CheckCircle2, Clock, ChevronRight, Home, Shield } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { LegalDocCard } from '@/components/Cards';
import { legalDocs } from '@/data/index';
import type { LegalDoc } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

type FilterStatus = 'all' | 'Đã ban hành' | 'Đang xử lý';

export default function Legal() {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  const filteredDocs = legalDocs.filter((doc) => {
    if (filterStatus === 'all') return true;
    return doc.status === filterStatus;
  });

  const issuedCount = legalDocs.filter((doc) => doc.status === 'Đã ban hành').length;
  const pendingCount = legalDocs.filter((doc) => doc.status === 'Đang xử lý').length;

  const legalBasis = [
    {
      name: 'Luật Nhà ở 2023',
      description: 'Có hiệu lực từ 01/08/2024, Điều 76 quy định 9 nhóm đối tượng được ưu tiên mua NOXH',
      effectiveDate: '01/08/2024',
    },
    {
      name: 'Nghị định 100/2024/NĐ-CP',
      description: 'Quy định chi tiết về phát triển và quản lý nhà ở xã hội; cơ sở pháp lý cho HARMONY Xuân Phương',
      effectiveDate: '26/07/2024',
    },
    {
      name: 'Quyết định UBND TP. Hà Nội',
      description: 'Phê duyệt quy hoạch Khu Chức Năng Đô Thị Xuân Phương, ô đất OCT1 & OCT3',
      effectiveDate: '2024',
    },
    {
      name: 'Nghị định 54/2026/NĐ-CP',
      description: 'Đơn giản hóa điều kiện cư trú, hỗ trợ xác minh qua VNeID và dữ liệu dân cư quốc gia',
      effectiveDate: '09/02/2026',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="relative h-[300px] overflow-hidden">
          <img
            src={IMAGES.HARMONY_RENDER_ANGLE}
            alt="HARMONY Xuân Phương – Hồ sơ pháp lý"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          <div className="relative container mx-auto px-4 h-full flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Home className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
                <span>Hồ Sơ Pháp Lý</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Hồ Sơ Pháp Lý</h1>
              <p className="text-lg text-muted-foreground">
                Minh bạch thông tin pháp lý dự án theo quy định của Luật Nhà ở 2023 và Nghị định 54/2026.
                Tất cả hồ sơ đã được thẩm định và phê duyệt bởi các cơ quan có thẩm quyền.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <Card className="bg-accent/10 border-accent/20">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{legalDocs.length}</div>
                      <div className="text-sm text-muted-foreground">Tổng số hồ sơ</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{issuedCount}</div>
                      <div className="text-sm text-muted-foreground">Đã ban hành</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      <Clock className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{pendingCount}</div>
                      <div className="text-sm text-muted-foreground">Đang xử lý</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Tabs defaultValue="all" className="mb-8">
              <TabsList className="grid w-full max-w-md grid-cols-3">
                <TabsTrigger value="all" onClick={() => setFilterStatus('all')}>
                  Tất cả
                </TabsTrigger>
                <TabsTrigger value="issued" onClick={() => setFilterStatus('Đã ban hành')}>
                  Đã ban hành
                </TabsTrigger>
                <TabsTrigger value="pending" onClick={() => setFilterStatus('Đang xử lý')}>
                  Đang xử lý
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {filteredDocs.map((doc, index) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <LegalDocCard doc={doc} />
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold">Cơ Sở Pháp Lý</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {legalBasis.map((basis, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + 0.1 * index }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <Badge className="mb-3">{basis.effectiveDate}</Badge>
                      <h3 className="text-lg font-semibold mb-2">{basis.name}</h3>
                      <p className="text-sm text-muted-foreground">{basis.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="bg-muted/50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Lưu Ý Quan Trọng</h3>
                    <p className="text-muted-foreground mb-4">
                      Để xem chi tiết và tải xuống bản gốc các hồ sơ pháp lý, quý khách vui lòng liên hệ trực tiếp
                      tại văn phòng chủ đầu tư hoặc gửi yêu cầu qua email. Tất cả hồ sơ đều được lưu trữ và công khai
                      theo quy định của pháp luật.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button variant="outline">
                        <Download className="w-4 h-4 mr-2" />
                        Tải Danh Mục Hồ Sơ
                      </Button>
                      <Button variant="outline">Liên Hệ Văn Phòng</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
